import { useEffect, useMemo } from "react";

import { SectionRole } from "../../../../shared/types/roles";

import {
  calculateStudentOutcomeScore,
  filterAssessmentsForStudent,
  getPageSpecificAssessmentData,
} from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import { getPageType, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { AssessmentPublishStatusKey } from "i18n/i18next";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  activitySelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  selectCurrentSectionId,
  selectFullSectionOutcomes,
  selectPagesWithAssessments,
  selectUsersInCurrentSection,
} from "store/selectors";
import { NestedSectionOutcomeType, UserWithSectionUserType } from "store/selectors/types";
import { averageAndRound } from "utils/math";
import { useQueryParamsManager } from "utils/routing";
import { getUserFullName } from "utils/user-utils";

export const useIsAssessing = () => {
  const { getQueryParam } = useQueryParamsManager();
  const { canAuthorSection } = useCapabilities();

  if (!canAuthorSection) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  const isAssessing = getQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);
  return !!isAssessing;
};

export type AllAssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: Array<{
    id: string;
    hidden: boolean;
    title: string;
    topicTitle: string | null | undefined;
    dueAt: string | null;
    submissionsLastCollectedAt: string | null;
    status: AssessmentStatus;
    unassessedLateCount: number;
    assessedSubmissionsCount: number;
    totalSubmissionsCount: number;
    isAssessmentComplete: boolean;
    isMarkedComplete: boolean;
    publishStatus: AssessmentPublishStatusKey;
    assessedCount: number;
    pageType: string;
    avgPoints: string;
    outcomesCount: number;
    outcomesTooltip: string;
    lastSubmittedDate: string;
  }>;
};

export type SectionScoreDashboardDataType = {
  studentPointsPerPageCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  studentPointsPerPageTableRows: Array<{
    page: PageType;
    id: string;
    title: string;
    pageType: string;
    avgPoints: string;
    studentScores: (string | number)[];
  }>;
  studentScoresPerOutcomeCSVData: {
    headers: string[];
    rows: (string | number | null)[][];
  };
  studentScoresPerOutcomeTableRows: Array<{
    student: UserWithSectionUserType;
    studentName: string;
    outcomeScores: (number | null)[];
  }>;
  sectionStudents: UserWithSectionUserType[];
  sectionOutcomes: NestedSectionOutcomeType[];
  publishedPagesWithAssessments: PageType[];
};

export const useSectionScoreData = (): SectionScoreDashboardDataType => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionUsers = useAppSelector(selectUsersInCurrentSection);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);
  const pageOutcomes = useAppSelector(pageOutcomeSelectors.selectAll);
  const pageGroupUsers = useAppSelector(pageGroupUserSelectors.selectAll);
  const pagesWithAssessments = useAppSelector(selectPagesWithAssessments);
  const activityMap = useAppSelector(activitySelectors.selectEntities);

  useEffect(() => {
    if (sectionId) {
      storeApi.section_users.list({ section_id: sectionId });
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.page_groups.list({ section_id: sectionId });
      storeApi.page_group_users.list({ section_id: sectionId });
      storeApi.submissions.list({ section_id: sectionId });
      storeApi.topics.list({ section_id: sectionId });
      storeApi.activities.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const sectionStudents = sectionUsers
    .filter((u) => u.role === SectionRole.STUDENT)
    .sort((a, b) => getUserFullName(a).localeCompare(getUserFullName(b)));

  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  const publishedPagesWithAssessments = pagesWithAssessments.filter(
    (page) => page.assessments_published_at && page.max_points,
  );
  const allScoredSubmissions = allAssessments.map((a) => a.submission);

  const studentPointsPerPageTableRows = useMemo(
    () =>
      publishedPagesWithAssessments.map((page) => {
        const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
        const studentScores = sectionStudents.map((student) => {
          const assessmentsForUser = filterAssessmentsForStudent(
            page.assessments,
            student,
            pageGroupUsers,
          );
          // Technically, this should always only be a single score,
          // but we average it (mean value) just in case
          const pointsForUser = assessmentsForUser
            .filter((a) => a.points !== null && a.points !== undefined)
            .map((a) => a.points);
          return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
        });
        return {
          page,
          id: page.id,
          title: page.title,
          pageType: getPageType(page, activityMap),
          avgPoints: pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
          studentScores,
        };
      }),
    [publishedPagesWithAssessments, pageOutcomes, activityMap, sectionStudents, pageGroupUsers],
  );

  const studentPointsPerPageCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.page_header_title"),
      t("assessment_dashboard.all.page_type_header_title"),
      t("assessment_dashboard.all.avg_points_header_title"),
      ...sectionStudents.map((student) => getUserFullName(student)),
    ];

    const rows = publishedPagesWithAssessments.map((page) => {
      const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
      const studentScores = sectionStudents.map((student) => {
        const assessmentsForUser = filterAssessmentsForStudent(
          page.assessments,
          student,
          pageGroupUsers,
        );
        const pointsForUser = assessmentsForUser
          .filter((a) => a.points !== null && a.points !== undefined)
          .map((a) => a.points);
        return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
      });

      return [
        page.title,
        getPageType(page, activityMap),
        pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
        ...studentScores,
      ];
    });

    return { headers, rows };
  }, [publishedPagesWithAssessments, sectionStudents, pageGroupUsers, activityMap, pageOutcomes]);

  // TODO: Only show outcome scores for finalized assessments
  const studentScoresPerOutcomeTableRows = useMemo(
    () =>
      sectionStudents.map((student) => {
        const userGroupIds = pageGroupUsers
          .filter((pgu) => pgu.section_user_id === student.section_user_id)
          .map((pgu) => pgu.page_group_id);

        const userSubmissions = allScoredSubmissions.filter(
          (submission) =>
            submission.section_user_id === student.section_user_id ||
            (submission.page_group_id && userGroupIds.includes(submission.page_group_id)),
        );

        const userAssessments = allAssessments.filter((a) =>
          userSubmissions.find((submission) => submission.id === a.submission_id),
        );

        const outcomeScores = sectionOutcomes.map((sectionOutcome) => {
          return calculateStudentOutcomeScore(userAssessments, sectionOutcome.id, pageOutcomes);
        });

        return {
          student,
          studentName: getUserFullName(student),
          outcomeScores,
        };
      }),
    [
      sectionStudents,
      sectionOutcomes,
      pageGroupUsers,
      allScoredSubmissions,
      allAssessments,
      pageOutcomes,
    ],
  );

  const studentScoresPerOutcomeCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.student_name_header_title"),
      ...sectionOutcomes.map((sectionOutcome) => displayOutcomeName(sectionOutcome.outcome)),
    ];

    const rows = sectionStudents.map((student) => {
      const userGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === student.section_user_id)
        .map((pgu) => pgu.page_group_id);

      const userSubmissions = allScoredSubmissions.filter(
        (submission) =>
          submission.section_user_id === student.section_user_id ||
          (submission.page_group_id && userGroupIds.includes(submission.page_group_id)),
      );

      const userAssessments = allAssessments.filter((assessment) =>
        userSubmissions.find((submission) => submission.id === assessment.submission_id),
      );

      const outcomeScores = sectionOutcomes.map((sectionOutcome) => {
        return calculateStudentOutcomeScore(userAssessments, sectionOutcome.id, pageOutcomes);
      });

      return [getUserFullName(student), ...outcomeScores];
    });

    return { headers, rows };
  }, [
    sectionStudents,
    sectionOutcomes,
    pageGroupUsers,
    allScoredSubmissions,
    allAssessments,
    pageOutcomes,
  ]);

  return {
    studentPointsPerPageCSVData,
    studentPointsPerPageTableRows,
    studentScoresPerOutcomeCSVData,
    studentScoresPerOutcomeTableRows,
    sectionStudents,
    sectionOutcomes,
    publishedPagesWithAssessments,
  };
};
